import { useState } from 'react';
import {
  configurarLeituraPorImagem,
  obterLeituraPorImagem,
  PARAMETROS_PADRAO,
} from '../../api/configuracao-matching.js';
import type {
  ConfiguracaoDeLeituraPorImagem,
  ModoDeCandidatos,
} from '../../api/configuracao-matching.js';
import { mensagemDoErro } from '../../api/erros.js';
import { useConsulta } from '../../api/useConsulta.js';
import { usePode } from '../../autorizacao/usePode.js';
import { AvisoDaTela } from '../../componentes/AvisoDaTela.js';
import type { TomDoAviso } from '../../componentes/AvisoDaTela.js';
import { Botao } from '../../componentes/Botao.js';
import { CampoSelecao, CampoTexto } from '../../componentes/Campo.js';
import { Cartao } from '../../componentes/Cartao.js';
import { Chave } from '../../componentes/Chave.js';
import { ChipStatus } from '../../componentes/ChipStatus.js';
import { DialogoConfirmacao } from '../../componentes/DialogoConfirmacao.js';
import { FalhaDeCarregamento } from '../../componentes/FalhaDeCarregamento.js';
import { seHouver } from '../../componentes/props.js';
import {
  PARAMETROS_DO_FORMULARIO,
  rascunhoDosParametros,
  validarParametros,
} from './parametros-de-leitura.js';
import type {
  ChaveDoParametro,
  ErrosDosParametros,
  RascunhoDosParametros,
} from './parametros-de-leitura.js';

const MODOS = [
  { valor: 'SALDO_FILIAL', rotulo: 'Saldo da filial' },
  { valor: 'CATALOGO', rotulo: 'Catálogo' },
];

const AJUDA_DO_MODO: Readonly<Record<ModoDeCandidatos, string>> = {
  SALDO_FILIAL:
    'Compara a foto com os produtos que têm saldo em algum depósito da filial. É o padrão.',
  CATALOGO:
    'Compara com todo o cadastro ativo, para produto sem saldo no ERP. Lista maior: o coletor demora mais e acerta menos sozinho.',
};

type Rascunho = {
  readonly modo: ModoDeCandidatos;
  readonly parametros: RascunhoDosParametros;
};

type Aviso = { readonly tom: TomDoAviso; readonly texto: string };

function rascunhoDe(configuracao: ConfiguracaoDeLeituraPorImagem): Rascunho {
  return { modo: configuracao.modo, parametros: rascunhoDosParametros(configuracao.parametros) };
}

function iguais(a: Rascunho, b: Rascunho): boolean {
  return (
    a.modo === b.modo &&
    PARAMETROS_DO_FORMULARIO.every(({ chave }) => a.parametros[chave] === b.parametros[chave])
  );
}

function EfeitoDaChave({ ligar }: { readonly ligar: boolean }) {
  return ligar ? (
    <>
      Nos inventários, o coletor passa a mostrar &quot;Não consegui ler&quot; quando o QR não sai,
      oferece a foto da etiqueta para marcar os cantos e, com rede, &quot;Identificar no
      servidor&quot;. Na dúvida, o operador confirma o produto antes de somar. A leitura normal não
      muda.
    </>
  ) : (
    <>
      O coletor deixa de mostrar &quot;Não consegui ler&quot;, a foto da etiqueta e
      &quot;Identificar no servidor&quot;. Etiqueta que não ler vai para a digitação do código. As
      contagens já feitas por imagem continuam valendo.
    </>
  );
}

/**
 * Leitura por imagem no inventário (qr-code.md §6.1 e §9.7): o fallback que
 * identifica o produto pela foto quando o QR não lê.
 *
 * A chave é o principal — nasce desligada e, desligada, o coletor nem mostra o
 * fallback. Ligar e desligar pedem confirmação porque mudam o que o operador vê
 * no meio da contagem. O modo de candidatos fica à vista; os limiares, que só
 * se mexem calibrando com fotos reais (§12.2), ficam recolhidos em Avançado.
 *
 * Supervisor lê; só quem gerencia a integração muda (o `PUT` é só do
 * Administrador). Perfil Consulta não vê o cartão — a API recusaria a leitura.
 */
export function LeituraPorImagem() {
  const pode = usePode();
  const podeVer = pode('leituraPorImagem.ver');
  const podeGerenciar = pode('integracao.gerenciar');

  const consulta = useConsulta(podeVer ? obterLeituraPorImagem : null);
  const [rascunho, definirRascunho] = useState<Rascunho | null>(null);
  const [erros, definirErros] = useState<ErrosDosParametros>({});
  const [avancadoAberto, definirAvancadoAberto] = useState(false);
  const [confirmando, definirConfirmando] = useState(false);
  const [agindo, definirAgindo] = useState(false);
  const [erroDoDialogo, definirErroDoDialogo] = useState<string | null>(null);
  const [aviso, definirAviso] = useState<Aviso | null>(null);

  if (!podeVer) {
    return null;
  }

  const { dados, recarregar } = consulta;

  function gravar(
    configuracao: ConfiguracaoDeLeituraPorImagem,
    aoConcluir: () => void,
    aoFalhar: (mensagem: string) => void,
  ) {
    definirAgindo(true);
    configurarLeituraPorImagem(configuracao)
      .then(() => {
        definirAgindo(false);
        aoConcluir();
        recarregar();
      })
      .catch((falha: unknown) => {
        definirAgindo(false);
        aoFalhar(mensagemDoErro(falha));
      });
  }

  function alternar(vigente: ConfiguracaoDeLeituraPorImagem) {
    const ligar = !vigente.habilitado;
    definirErroDoDialogo(null);

    // Só a chave muda: um rascunho de parâmetros ainda não salvo não pega
    // carona numa confirmação que fala de outra coisa.
    gravar(
      { ...vigente, habilitado: ligar },
      () => {
        definirConfirmando(false);
        definirAviso({
          tom: 'ok',
          texto: ligar
            ? 'O coletor passa a oferecer a leitura por imagem nos inventários.'
            : 'O coletor deixa de oferecer a leitura por imagem.',
        });
      },
      definirErroDoDialogo,
    );
  }

  function salvar(vigente: ConfiguracaoDeLeituraPorImagem, atual: Rascunho) {
    const resultado = validarParametros(atual.parametros);

    if (!resultado.valido) {
      definirErros(resultado.erros);
      definirAvancadoAberto(true);
      return;
    }

    definirErros({});
    definirAviso(null);
    gravar(
      { habilitado: vigente.habilitado, modo: atual.modo, parametros: resultado.parametros },
      () => {
        definirRascunho(null);
        definirAviso({ tom: 'ok', texto: 'Parâmetros salvos. O coletor recebe na próxima carga.' });
      },
      (mensagem) => {
        definirAviso({ tom: 'erro', texto: mensagem });
      },
    );
  }

  function mudarParametro(atual: Rascunho, chave: ChaveDoParametro, valor: string) {
    definirAviso(null);
    definirRascunho({ ...atual, parametros: { ...atual.parametros, [chave]: valor } });
  }

  function desfazer() {
    definirRascunho(null);
    definirErros({});
  }

  const gravado = dados === null ? null : rascunhoDe(dados);
  const atual = rascunho ?? gravado;
  const mudou = atual !== null && gravado !== null && !iguais(atual, gravado);

  return (
    <Cartao titulo="Leitura por imagem no inventário">
      <p className="configuracoes__nota">
        Quando o QR da etiqueta não lê — pilha alta, etiqueta em ângulo —, o coletor identifica o
        produto pela foto, comparando com os produtos da filial. Na dúvida, pergunta ao operador.
      </p>

      {aviso !== null && <AvisoDaTela tom={aviso.tom}>{aviso.texto}</AvisoDaTela>}

      {consulta.erro !== null && (
        <FalhaDeCarregamento mensagem={consulta.erro} aoTentarDeNovo={recarregar} />
      )}

      {consulta.erro === null && dados === null && (
        <p className="configuracoes__detalhe" role="status">
          Carregando a leitura por imagem…
        </p>
      )}

      {dados !== null && atual !== null && (
        <>
          <p className="configuracoes__estado">
            {podeGerenciar ? (
              <Chave
                rotulo="Leitura por imagem"
                ligada={dados.habilitado}
                desabilitada={agindo}
                aoAlternar={() => {
                  definirAviso(null);
                  definirConfirmando(true);
                }}
              />
            ) : (
              <ChipStatus
                tom={dados.habilitado ? 'concluida' : 'desativado'}
                rotulo={dados.habilitado ? 'Ligada' : 'Desligada'}
              />
            )}
            <span className="configuracoes__detalhe">
              {dados.habilitado
                ? 'O coletor oferece a foto quando o QR não lê.'
                : 'O coletor só lê o QR ou aceita o código digitado.'}
            </span>
          </p>

          <div className="configuracoes__grade configuracoes__grade--depois">
            <CampoSelecao
              rotulo="Candidatos"
              valor={atual.modo}
              opcoes={MODOS}
              aoMudar={(valor) => {
                definirAviso(null);
                definirRascunho({
                  ...atual,
                  modo: valor === 'CATALOGO' ? 'CATALOGO' : 'SALDO_FILIAL',
                });
              }}
              desabilitado={!podeGerenciar}
              ajuda={AJUDA_DO_MODO[atual.modo]}
            />
          </div>

          <details
            className="configuracoes__avancado"
            open={avancadoAberto}
            onToggle={(evento) => {
              definirAvancadoAberto(evento.currentTarget.open);
            }}
          >
            <summary className="configuracoes__resumo">Avançado</summary>
            <p className="configuracoes__nota">
              Limiares do reconhecimento. Os padrões são o ponto de partida; mude só calibrando com
              fotos reais do armazém.
            </p>
            <div className="configuracoes__grade">
              {PARAMETROS_DO_FORMULARIO.map(({ chave, rotulo, ajuda }) => (
                <CampoTexto
                  key={chave}
                  rotulo={rotulo}
                  valor={atual.parametros[chave]}
                  aoMudar={(valor) => {
                    mudarParametro(atual, chave, valor);
                  }}
                  fonte="mono"
                  desabilitado={!podeGerenciar}
                  ajuda={ajuda}
                  {...seHouver('erro', erros[chave])}
                />
              ))}
            </div>
            {podeGerenciar && (
              <div className="configuracoes__acoes">
                <Botao
                  variante="discreto"
                  onClick={() => {
                    definirErros({});
                    definirRascunho({
                      ...atual,
                      parametros: rascunhoDosParametros(PARAMETROS_PADRAO),
                    });
                  }}
                >
                  Restaurar os padrões
                </Botao>
              </div>
            )}
          </details>

          {podeGerenciar && mudou && (
            <div className="configuracoes__acoes">
              <Botao
                variante="primario"
                disabled={agindo}
                onClick={() => {
                  salvar(dados, atual);
                }}
              >
                {agindo ? 'Salvando…' : 'Salvar'}
              </Botao>
              <Botao variante="discreto" onClick={desfazer}>
                Desfazer
              </Botao>
            </div>
          )}
        </>
      )}

      {confirmando && dados !== null && (
        <DialogoConfirmacao
          titulo={
            dados.habilitado
              ? 'Desligar a leitura por imagem?'
              : 'Ligar a leitura por imagem no inventário?'
          }
          rotuloConfirmar={dados.habilitado ? 'Desligar' : 'Ligar'}
          variante={dados.habilitado ? 'atencao' : 'primario'}
          agindo={agindo}
          aoConfirmar={() => {
            alternar(dados);
          }}
          aoCancelar={() => {
            definirConfirmando(false);
            definirErroDoDialogo(null);
          }}
        >
          <EfeitoDaChave ligar={!dados.habilitado} />
          {erroDoDialogo !== null && (
            <span className="configuracoes__erro-dialogo">{erroDoDialogo}</span>
          )}
        </DialogoConfirmacao>
      )}
    </Cartao>
  );
}
